import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./MaintenancePage.scss";

const STATUS_REFRESH_INTERVAL_MS = 30_000;

function getRemainingSeconds(maintenanceEnd: number): number {
    return Math.max(0, Math.ceil((maintenanceEnd - Date.now()) / 1000));
}

interface MaintenancePageProps {
    maintenanceEnd: number;
}

export default function MaintenancePage({
    maintenanceEnd,
}: MaintenancePageProps) {
    const { t } = useTranslation();
    const [remainingSeconds, setRemainingSeconds] = useState(() =>
        getRemainingSeconds(maintenanceEnd),
    );

    useEffect(() => {
        const updateCountdown = () => {
            setRemainingSeconds(getRemainingSeconds(maintenanceEnd));
        };

        updateCountdown();
        const countdownTimer = window.setInterval(updateCountdown, 1_000);
        const statusRefreshTimer = window.setInterval(() => {
            window.location.reload();
        }, STATUS_REFRESH_INTERVAL_MS);

        return () => {
            window.clearInterval(countdownTimer);
            window.clearInterval(statusRefreshTimer);
        };
    }, [maintenanceEnd]);

    const hours = Math.floor(remainingSeconds / 3_600);
    const minutes = Math.floor((remainingSeconds % 3_600) / 60);
    const seconds = remainingSeconds % 60;

    return (
        <main className="maintenance-page">
            <section className="maintenance-page__panel">
                <p className="maintenance-page__eyebrow">
                    {t("maintenance.estimatedTime")}
                </p>
                <h1 className="maintenance-page__title">
                    {t("maintenance.title")}
                </h1>
                <p className="maintenance-page__description">
                    {t("maintenance.description")}
                </p>

                <div
                    className="maintenance-page__countdown"
                    role="timer"
                    dir="ltr"
                    aria-label={`${hours} ${t("maintenance.hours")}, ${minutes} ${t("maintenance.minutes")}, ${seconds} ${t("maintenance.seconds")}`}
                >
                    <div className="maintenance-page__time-part">
                        <span className="maintenance-page__time-value">
                            {String(hours).padStart(2, "0")}
                        </span>
                        <span className="maintenance-page__time-label">
                            {t("maintenance.hours")}
                        </span>
                    </div>
                    <span
                        className="maintenance-page__separator"
                        aria-hidden="true"
                    >
                        :
                    </span>
                    <div className="maintenance-page__time-part">
                        <span className="maintenance-page__time-value">
                            {String(minutes).padStart(2, "0")}
                        </span>
                        <span className="maintenance-page__time-label">
                            {t("maintenance.minutes")}
                        </span>
                    </div>
                    <span
                        className="maintenance-page__separator"
                        aria-hidden="true"
                    >
                        :
                    </span>
                    <div className="maintenance-page__time-part">
                        <span className="maintenance-page__time-value">
                            {String(seconds).padStart(2, "0")}
                        </span>
                        <span className="maintenance-page__time-label">
                            {t("maintenance.seconds")}
                        </span>
                    </div>
                </div>
            </section>
        </main>
    );
}
